/**
 * TriggersPanel.tsx — the ⚡ trigger management surface (the trigger
 * contract's UI half).
 *
 * One modal listing EVERY trigger in the active graph's bundle: arm/disarm
 * without deleting, ▶ Fire now, live status (last fired step / last error,
 * fed by the relay path's `triggerEvent` broadcasts), an inline dry-run
 * TEST (the expr evaluated over the captured tick history client-side with
 * the same walker + firing semantics the backend uses), delete — and a
 * full EDITOR (the TriggerSettingsModal: condition builder + dry-run +
 * workflow picker) for add/edit, the same modal the NodeModal's footer
 * "Trigger settings" button opens. Writes go through the
 * `setWorkflowTriggers` op (whole-list replace).
 */
import { useState } from 'react';
import { Lightning as Bolt, Plus, Trash, PencilSimple, Play, Flask } from '@phosphor-icons/react';

import type { ScenarioBundle, TickPayload, WorkflowTrigger } from '../engine/types';
import { Modal } from './Modal';
import type { RemoteProjectAdapter } from '../projects/RemoteProjectAdapter';
import { TriggerEditor } from './TriggerSettingsModal';
import { dryRunTrigger } from '../workflow/triggerTools';

export interface TriggerStatusMap {
  [triggerId: string]: { lastFiredStep: number | null; lastError: string | null; at: number };
}

interface TriggersPanelProps {
  bundle: ScenarioBundle;
  editable: boolean;
  projectAdapter?: RemoteProjectAdapter;
  projectId?: string;
  /** Captured tick history — the dry-run's dataset (same walker + semantics
   *  as the backend's relay-path evaluator). */
  recentTicks: TickPayload[];
  /** Live trigger status (last fired / last error per trigger id). */
  triggerStatus: TriggerStatusMap;
  /** Fire a workflow run NOW: ensures the worker is up, starts the run,
   *  rejects with a readable reason on failure. */
  fireTrigger: (workflow: string, input: unknown[]) => Promise<void>;
  onClose: () => void;
  /** Replace the bundle's whole trigger list (the setWorkflowTriggers op). */
  onSetWorkflowTriggers?: (triggers: WorkflowTrigger[]) => void;
}

export function TriggersPanel({
  bundle, editable, projectAdapter, projectId, recentTicks, triggerStatus,
  fireTrigger, onClose, onSetWorkflowTriggers,
}: TriggersPanelProps): JSX.Element {
  const triggers = bundle.workflowTriggers ?? [];
  // The inline editor slot: -1 = create, >= 0 = the index being edited. When
  // set, the editor replaces the list INSIDE this modal (no second modal).
  const [editing, setEditing] = useState<number | null>(null);
  const [testing, setTesting] = useState<string | null>(null); // trigger id
  const [fireError, setFireError] = useState<string | null>(null);
  const [firing, setFiring] = useState<string | null>(null);

  const writeAll = (next: WorkflowTrigger[]) => {
    onSetWorkflowTriggers?.(next.length > 0 ? next : []);
  };

  const fire = (t: WorkflowTrigger) => {
    setFireError(null);
    setFiring(t.id);
    fireTrigger(t.workflow, t.input ?? [])
      .catch((e) => setFireError(`${t.id}: ${e instanceof Error ? e.message : String(e)}`))
      .finally(() => setFiring(null));
  };

  return (
    <Modal title="Triggers" onClose={editing !== null ? () => setEditing(null) : onClose}>
      {editing !== null && editable ? (
        <TriggerEditor
          initial={editing >= 0 ? triggers[editing] : undefined}
          bundle={bundle}
          editable={editable}
          projectAdapter={projectAdapter}
          projectId={projectId}
          nodeOptions={bundle.graph.nodes.map((n) => ({ id: n.id, label: n.label }))}
          recentTicks={recentTicks}
          onCancel={() => setEditing(null)}
          onSave={(next) => {
            if (editing < 0) writeAll([...triggers, next]);
            else writeAll(triggers.map((t, j) => (j === editing ? next : t)));
            setEditing(null);
          }}
        />
      ) : (
      <div className="trigger-list">
        {fireError && <p className="trigger-fire-error">{fireError}</p>}
        {triggers.length === 0 && (
          <p className="agents-empty">No triggers in this graph — add one below.</p>
        )}
        {triggers.map((t, i) => {
          const st = triggerStatus[t.id];
          const disarmed = t.enabled === false;
          const dry = testing === t.id && t.expr
            ? dryRunTrigger(t.expr, recentTicks, t.cooldownTicks ?? 96)
            : null;
          return (
            <div key={t.id}>
              <div className="trigger-row">
                <button
                  type="button" className="code-task-btn"
                  title={disarmed ? 'Disarmed — click to arm' : 'Armed — click to disarm'}
                  disabled={!editable}
                  style={{ opacity: disarmed ? 0.4 : 1 }}
                  onClick={() => writeAll(triggers.map((x, j) =>
                    j === i ? { ...x, enabled: disarmed ? undefined : false } : x))}
                >
                  <Bolt size={14} />
                </button>
                <div className="trigger-row-main">
                  <div className="trigger-row-title">
                    {t.label || t.id} → {t.workflow}
                    {t.on === 'manual' && <span className="agents-mode"> manual</span>}
                    {t.on === 'cron' && <span className="agents-mode"> cron</span>}
                  </div>
                  <div className="trigger-row-meta">
                    {t.on === 'expr' && <code>{t.expr}</code>}
                    {t.on === 'cron' && <code>{t.schedule}</code>}
                    {disarmed && <span className="agents-fence read-only">disarmed</span>}
                    {st?.lastError
                      ? <span className="trigger-status-error" title={st.lastError}>last fire FAILED</span>
                      : st?.lastFiredStep != null
                        ? <span>fired @ step {st.lastFiredStep}</span>
                        : <span className="param-hint">never fired</span>}
                  </div>
                </div>
                <button
                  type="button" className="code-task-btn" title="Dry-run over captured ticks"
                  disabled={!t.expr || recentTicks.length === 0}
                  onClick={() => setTesting(testing === t.id ? null : t.id)}
                >
                  <Flask size={14} />
                </button>
                <button
                  type="button" className="code-task-btn" title="Fire now"
                  disabled={firing === t.id}
                  onClick={() => fire(t)}
                >
                  <Play size={14} />
                </button>
                {editable && (
                  <>
                    <button
                      type="button" className="code-task-btn" title="Edit (condition builder)"
                      onClick={() => { setTesting(null); setEditing(i); }}
                    >
                      <PencilSimple size={14} />
                    </button>
                    <button
                      type="button" className="code-task-btn danger" title="Delete"
                      onClick={() => writeAll(triggers.filter((_, j) => j !== i))}
                    >
                      <Trash size={13} />
                    </button>
                  </>
                )}
              </div>
              {dry && (
                <div className="param-hint" style={{ padding: '4px 6px' }}>
                  {dry.error
                    ? <span className="trigger-dryrun-error">expr error: {dry.error}</span>
                    : <>
                        {recentTicks.length} ticks: holds at {dry.holds.length}, fires{' '}
                        <strong>{dry.fires.length}</strong>
                        {dry.fires.length > 0 && <> — steps {dry.fires.slice(0, 12).join(', ')}{dry.fires.length > 12 ? '…' : ''}</>}
                        {dry.current ? ' · holds NOW' : ''}
                      </>}
                </div>
              )}
            </div>
          );
        })}
        {editable && editing === null && (
          <button type="button" className="btn agents-add" onClick={() => setEditing(-1)}>
            <Plus size={14} /> Add trigger
          </button>
        )}
      </div>
      )}
    </Modal>
  );
}
